import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UploadSimple, FileXls, CheckCircle, WarningCircle, Sparkle } from "@phosphor-icons/react";

export default function BulkImportDialog({ open, onOpenChange, companyId, cycleId, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [useAI, setUseAI] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("company_id", companyId);
      fd.append("cycle_id", cycleId);
      fd.append("use_ai", String(useAI));
      fd.append("file", file);
      const r = await api.post("/observations/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(r.data);
      if (r.data.imported > 0) {
        toast.success(`Imported ${r.data.imported} observation(s)`);
        onImported?.();
        window.dispatchEvent(new Event("activities-reload"));
      } else {
        toast.error("No observations imported");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadSimple size={18} /> Bulk import observations
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
            <div className="font-semibold text-slate-800 mb-1">Expected Excel format</div>
            Header row with columns: <span className="mono">Area / Department</span>,{" "}
            <span className="mono">Observation</span>, <span className="mono">Impact</span>,{" "}
            <span className="mono">Recommendation</span>, <span className="mono">Risk</span> (optional).
            Rows below the header are imported. Blank rows are skipped.
          </div>

          <div>
            <Label>Excel File (.xlsx)</Label>
            <div className="mt-1 border border-dashed border-slate-300 rounded-md p-6 text-center hover:border-blue-400 transition cursor-pointer" onClick={() => fileRef.current?.click()}>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-import-file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-slate-700">
                  <FileXls size={20} className="text-emerald-600" />
                  <div>
                    <div className="font-medium">{file.name}</div>
                    <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB · click to change</div>
                  </div>
                </div>
              ) : (
                <div className="text-slate-500">
                  <FileXls size={28} className="mx-auto text-slate-400 mb-2" />
                  <div className="text-sm font-medium text-slate-700">Click to choose Excel file</div>
                  <div className="text-xs">or drag and drop (max 10 MB)</div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between border border-slate-200 rounded-md p-3 bg-blue-50/30">
            <div>
              <div className="text-sm font-medium flex items-center gap-1.5">
                <Sparkle size={13} className="text-blue-600" /> AI-assist on import
              </div>
              <div className="text-xs text-slate-600 mt-0.5">When Risk/Impact/Recommendation are blank, Claude Sonnet 4.5 fills them in.</div>
            </div>
            <Switch checked={useAI} onCheckedChange={setUseAI} data-testid="bulk-import-ai-toggle" />
          </div>

          {result && (
            <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="bulk-import-result">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-emerald-600" />
                <div className="text-sm font-medium">
                  {result.imported} imported{result.errors?.length ? `, ${result.errors.length} skipped` : ""}
                </div>
              </div>
              {!!result.errors?.length && (
                <ul className="mt-2 text-xs text-amber-700 space-y-0.5 max-h-40 overflow-auto">
                  {result.errors.slice(0, 20).map((e) => (
                    <li key={`err-row-${e.row}-${(e.error || '').slice(0, 20)}`} className="flex items-center gap-1">
                      <WarningCircle size={11} /> Row {e.row}: {e.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            className="bg-slate-900 hover:bg-slate-800"
            disabled={!file || busy}
            onClick={submit}
            data-testid="bulk-import-submit-btn"
          >
            {busy ? "Importing..." : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
